import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Box,
  FormControl,
  FormHelperText,
  Select,
  MenuItem,
  CircularProgress,
  Slide,
} from "@material-ui/core";
// Note: Select and MenuItem kept for Gender dropdown
import CloseIcon from "@material-ui/icons/Close";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import CheckIcon from "@material-ui/icons/Check";
import VisibilityIcon from "@material-ui/icons/Visibility";
import DescriptionIcon from "@material-ui/icons/Description";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import { MuiPickersUtilsProvider, KeyboardDatePicker } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { apiPath } from "api";
import { generalAuthTokenHeader, multimediaAuthTokenHeader } from "__helpers/auth-header";
import { userService } from "_services/user.service";
import moment from "moment";

import "./EditPatientModal.css";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

class EditPatientModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      // Form fields
      firstName: "",
      lastName: "",
      gender: "",
      dateOfBirth: null,
      estimateDischargeDate: null,
      insurance: "",
      insurancePolicyNum: "",
      referringFacility: "",
      address: "",
      city: "",
      zip: "",
      phone: "",
      primaryDiagnosis: "",
      referringPhysicianNpi: "",
      patientNote: "",

      // Form state
      loading: false,
      errors: {},

      // Copy to clipboard state
      copiedField: null,

      // Documents state
      documents: [],
      loadingDocuments: false,
      loadingDocumentId: null,
      deletingDocumentId: null,
      uploadingDocument: false,
    };

    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleCopyToClipboard = this.handleCopyToClipboard.bind(this);
    this.renderCopyButton = this.renderCopyButton.bind(this);
    this.handleDateChange = this.handleDateChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.populateForm = this.populateForm.bind(this);
    this.fetchPatientDetails = this.fetchPatientDetails.bind(this);
    this.handleViewDocument = this.handleViewDocument.bind(this);
    this.handleDeleteDocument = this.handleDeleteDocument.bind(this);
    this.handleDocumentUpload = this.handleDocumentUpload.bind(this);
  }
  
  componentDidUpdate(prevProps) {
    if (this.props.patient && this.props.patient !== prevProps.patient) {
      this.populateForm(this.props.patient);
    }
    if (this.props.open && !prevProps.open && this.props.patient) {
      this.populateForm(this.props.patient);
      this.fetchPatientDetails(this.props.patient.id);
    }
  }

  populateForm(patient) {
    if (!patient) return;

    this.setState({
      firstName: patient.firstName || "",
      lastName: patient.lastName || "",
      gender: patient.gender || "",
      dateOfBirth: patient.dateOfBirth ? new Date(patient.dateOfBirth) : null,
      estimateDischargeDate: patient.estimateDischargeDate ? new Date(patient.estimateDischargeDate) : null,
      insurance: patient.insurance || "",
      insurancePolicyNum: patient.insurancePolicyNum || "",
      referringFacility: patient.referringFacility || "",
      address: patient.address || "",
      city: patient.city || "",
      zip: patient.zip || "",
      phone: patient.phone || "",
      primaryDiagnosis: patient.primaryDiagnosis || "",
      referringPhysicianNpi: patient.referringPhysicianNpi || "",
      patientNote: patient.patientNote || "",
      errors: {},
    });
  }

  async fetchPatientDetails(patientId) {
    this.setState({ loadingDocuments: true, documents: [] });
    try {
      const response = await fetch(`${apiPath.externalPatients}/${patientId}`, {
        headers: generalAuthTokenHeader(),
      });
      if (response.ok) {
        const data = await response.json();
        this.setState({
          documents: data.uploadedDocuments || [],
          loadingDocuments: false,
        });
      } else {
        this.setState({ loadingDocuments: false });
      }
    } catch (error) {
      console.error("Error fetching patient details:", error);
      this.setState({ loadingDocuments: false });
    }
  }

  async handleViewDocument(doc) {
    this.setState({ loadingDocumentId: doc.id });
    try {
      const response = await fetch(
        `${apiPath.externalPatients}/${this.props.patient.id}/documents/${doc.id}/download`,
        { headers: generalAuthTokenHeader() }
      );
      if (response.ok) {
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, "_blank");
      } else {
        userService.showNotification({
          title: "Error",
          message: "Failed to load document",
          type: "danger",
        });
      }
    } catch (error) {
      console.error("Error viewing document:", error);
      userService.showNotification({
        title: "Error",
        message: "An error occurred while loading the document",
        type: "danger",
      });
    } finally {
      this.setState({ loadingDocumentId: null });
    }
  }

  async handleDeleteDocument(doc) {
    if (!window.confirm(`Are you sure you want to delete "${doc.uploadName}"?`)) {
      return;
    }

    this.setState({ deletingDocumentId: doc.id });
    try {
      const response = await fetch(
        `${apiPath.externalPatients}/${this.props.patient.id}/documents/${doc.id}`,
        {
          method: "DELETE",
          headers: generalAuthTokenHeader(),
        }
      );
      if (response.ok) {
        this.setState((prevState) => ({
          documents: prevState.documents.filter((d) => d.id !== doc.id),
        }));
        userService.showNotification({
          title: "Success",
          message: "Document deleted successfully",
          type: "success",
        });
      } else {
        userService.showNotification({
          title: "Error",
          message: "Failed to delete document",
          type: "danger",
        });
      }
    } catch (error) {
      console.error("Error deleting document:", error);
      userService.showNotification({
        title: "Error",
        message: "An error occurred while deleting the document",
        type: "danger",
      });
    } finally {
      this.setState({ deletingDocumentId: null });
    }
  }

  async handleDocumentUpload(event) {
    const { patient } = this.props;
    if (!patient || !event.target.files || event.target.files.length === 0) return;

    this.setState({ uploadingDocument: true });
    try {
      const formData = new FormData();
      for (let i = 0; i < event.target.files.length; i++) {
        formData.append("files", event.target.files[i]);
      }

      const headers = multimediaAuthTokenHeader();
      delete headers["Content-Type"];

      const response = await fetch(
        `${apiPath.externalPatients}/${patient.id}/documents`,
        {
          method: "POST",
          headers: headers,
          body: formData,
        }
      );

      if (response.ok || response.status === 202) {
        userService.showNotification({
          title: "Success",
          message: "Document uploaded successfully",
          type: "success",
        });
        this.fetchPatientDetails(patient.id);
      } else {
        userService.showNotification({
          title: "Error",
          message: "Failed to upload document",
          type: "danger",
        });
      }
    } catch (error) {
      console.error("Error uploading document:", error);
      userService.showNotification({
        title: "Error",
        message: "An error occurred while uploading the document",
        type: "danger",
      });
    } finally {
      this.setState({ uploadingDocument: false });
      event.target.value = "";
    }
  }

  handleCopyToClipboard(fieldName, value) {
    if (!value) return;

    const stringValue = typeof value === 'object' && value instanceof Date
      ? value.toLocaleDateString('en-US')
      : String(value);

    navigator.clipboard.writeText(stringValue).then(() => {
      this.setState({ copiedField: fieldName });
      setTimeout(() => {
        this.setState((prevState) => {
          if (prevState.copiedField === fieldName) {
            return { copiedField: null };
          }
          return null;
        });
      }, 1500);
    });
  }

  renderCopyButton(fieldName, value) {
    const { copiedField } = this.state;
    const isCopied = copiedField === fieldName;

    return (
      <IconButton
        className={`copy-btn ${isCopied ? 'copied' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          this.handleCopyToClipboard(fieldName, value);
        }}
        size="small"
        disabled={!value}
        title={isCopied ? "Copied!" : "Copy to clipboard"}
      >
        {isCopied ? <CheckIcon fontSize="small" /> : <FileCopyOutlinedIcon fontSize="small" />}
      </IconButton>
    );
  }

  handleInputChange(event) {
    const { name, value } = event.target;
    this.setState({ 
      [name]: value,
      errors: { ...this.state.errors, [name]: "" }
    });
  }
  
  handleDateChange(name, date) {
    this.setState({ 
      [name]: date,
      errors: { ...this.state.errors, [name]: "" }
    });
  }
  
  validateForm() {
    const errors = {};
    const { firstName, lastName, gender, dateOfBirth } = this.state;

    if (!firstName.trim()) errors.firstName = "First name is required";
    if (!lastName.trim()) errors.lastName = "Last name is required";
    if (!gender) errors.gender = "Gender is required";
    if (!dateOfBirth) errors.dateOfBirth = "Date of birth is required";

    this.setState({ errors });
    return Object.keys(errors).length === 0;
  }
  
  async handleSubmit(e) {
    e.preventDefault();
    
    if (!this.validateForm()) return;
    
    this.setState({ loading: true });
    
    try {
      const { patient } = this.props;
      const {
        firstName,
        lastName,
        gender,
        dateOfBirth,
        estimateDischargeDate,
        insurance,
        insurancePolicyNum,
        referringFacility,
        address,
        city,
        zip,
        phone,
        primaryDiagnosis,
        referringPhysicianNpi,
        patientNote,
      } = this.state;

      const requestBody = {
        firstName,
        lastName,
        gender: gender || null,
        dateOfBirth: dateOfBirth ? moment(dateOfBirth).format("YYYY-MM-DD") : null,
        estimateDischargeDate: estimateDischargeDate ? moment(estimateDischargeDate).format("YYYY-MM-DD") : null,
        insurance: insurance || null,
        insurancePolicyNum: insurancePolicyNum || null,
        referringFacility: referringFacility || null,
        address: address || null,
        city: city || null,
        zip: zip || null,
        phone: phone || null,
        primaryDiagnosis: primaryDiagnosis || null,
        referringPhysicianNpi: referringPhysicianNpi || null,
        patientNote: patientNote || null,
        status: "COMPLETED", // Mark as completed on edit
      };
      
      const response = await fetch(`${apiPath.externalPatients}/${patient.id}`, {
        method: "PUT",
        headers: generalAuthTokenHeader(),
        body: JSON.stringify(requestBody),
      });
      
      if (response.ok) {
        const data = await response.json();
        userService.showNotification({
          title: "Success",
          message: "Patient updated successfully",
          type: "success",
        });
        this.props.onPatientUpdated(data);
      } else {
        const errorData = await response.json();
        userService.showNotification({
          title: "Error",
          message: errorData.message || "Failed to update patient",
          type: "danger",
        });
        this.setState({
          errors: { submit: errorData.message || "Failed to update patient" }
        });
      }
    } catch (error) {
      console.error("Error updating patient:", error);
      userService.showNotification({
        title: "Error",
        message: "An error occurred while updating the patient",
        type: "danger",
      });
      this.setState({
        errors: { submit: "An error occurred while updating the patient" }
      });
    } finally {
      this.setState({ loading: false });
    }
  }

  render() {
    const { open, onClose, patient } = this.props;
    const {
      firstName,
      lastName,
      gender,
      dateOfBirth,
      estimateDischargeDate,
      insurance,
      insurancePolicyNum,
      referringFacility,
      address,
      city,
      zip,
      phone,
      primaryDiagnosis,
      referringPhysicianNpi,
      loading,
      errors,
      documents,
      loadingDocuments,
      loadingDocumentId,
      deletingDocumentId,
      uploadingDocument,
    } = this.state;
    
    return (
      <Dialog
        open={open}
        maxWidth="md"
        fullWidth
        onClose={onClose}
        TransitionComponent={Transition}
        className="edit-patient-modal"
      >
        <DialogTitle className="modal-title">
          <div>
            {patient?.status === "DRAFT" ? "Verify Details" : "Edit Details"}
            {patient?.status === "DRAFT" && (
              <p className="modal-subtitle">Please verify the details generated by AI to mark this patient complete.</p>
            )}
          </div>
          <IconButton className="close-button" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent className="modal-content">
          <form className="edit-form">
            <Box display="flex" flexWrap="wrap" className="form-row">
              {/* First Name */}
              <Box className="form-field half">
                <label>First Name <span style={{ color: 'red' }}>*</span></label>
                <div className="field-with-copy">
                  <TextField
                    name="firstName"
                    value={firstName}
                    onChange={this.handleInputChange}
                    variant="outlined"
                    fullWidth
                    error={!!errors.firstName}
                    helperText={errors.firstName}
                    placeholder="Enter first name"
                  />
                  {this.renderCopyButton('firstName', firstName)}
                </div>
              </Box>

              {/* Last Name */}
              <Box className="form-field half">
                <label>Last Name <span style={{ color: 'red' }}>*</span></label>
                <div className="field-with-copy">
                  <TextField
                    name="lastName"
                    value={lastName}
                    onChange={this.handleInputChange}
                    variant="outlined"
                    fullWidth
                    error={!!errors.lastName}
                    helperText={errors.lastName}
                    placeholder="Enter last name"
                  />
                  {this.renderCopyButton('lastName', lastName)}
                </div>
              </Box>
            </Box>
            
            <Box display="flex" flexWrap="wrap" className="form-row">
              {/* Gender */}
              <Box className="form-field half">
                <label>Gender <span style={{ color: 'red' }}>*</span></label>
                <div className="field-with-copy">
                  <FormControl variant="outlined" fullWidth error={!!errors.gender}>
                    <Select
                      name="gender"
                      value={gender}
                      onChange={this.handleInputChange}
                      displayEmpty
                    >
                      <MenuItem value="">Select Gender</MenuItem>
                      <MenuItem value="Male">Male</MenuItem>
                      <MenuItem value="Female">Female</MenuItem>
                      <MenuItem value="Others">Others</MenuItem>
                    </Select>
                    {errors.gender && <FormHelperText>{errors.gender}</FormHelperText>}
                  </FormControl>
                  {this.renderCopyButton('gender', gender)}
                </div>
              </Box>

              {/* Date of Birth */}
              <Box className="form-field half">
                <label>Date of Birth <span style={{ color: 'red' }}>*</span></label>
                <div className="field-with-copy">
                  <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <KeyboardDatePicker
                      format="MM/dd/yyyy"
                      value={dateOfBirth}
                      onChange={(date) => this.handleDateChange("dateOfBirth", date)}
                      inputVariant="outlined"
                      fullWidth
                      placeholder="MM/DD/YYYY"
                      error={!!errors.dateOfBirth}
                      helperText={errors.dateOfBirth}
                      KeyboardButtonProps={{ "aria-label": "change date" }}
                    />
                  </MuiPickersUtilsProvider>
                  {this.renderCopyButton('dateOfBirth', dateOfBirth)}
                </div>
              </Box>
            </Box>
            
            <Box display="flex" flexWrap="wrap" className="form-row">
              {/* Estimated Discharge Date */}
              <Box className="form-field half">
                <label>Estimated Discharge Date</label>
                <div className="field-with-copy">
                  <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <KeyboardDatePicker
                      format="MM/dd/yyyy"
                      value={estimateDischargeDate}
                      onChange={(date) => this.handleDateChange("estimateDischargeDate", date)}
                      inputVariant="outlined"
                      fullWidth
                      placeholder="MM/DD/YYYY"
                      KeyboardButtonProps={{ "aria-label": "change date" }}
                    />
                  </MuiPickersUtilsProvider>
                  {this.renderCopyButton('estimateDischargeDate', estimateDischargeDate)}
                </div>
              </Box>

              {/* Insurance */}
              <Box className="form-field half">
                <label>Insurance</label>
                <div className="field-with-copy">
                  <TextField
                    name="insurance"
                    value={insurance}
                    onChange={this.handleInputChange}
                    variant="outlined"
                    fullWidth
                    placeholder="Enter insurance name"
                  />
                  {this.renderCopyButton('insurance', insurance)}
                </div>
              </Box>
            </Box>

            <Box display="flex" flexWrap="wrap" className="form-row">
              {/* Insurance Policy # */}
              <Box className="form-field half">
                <label>Insurance Policy #</label>
                <div className="field-with-copy">
                  <TextField
                    name="insurancePolicyNum"
                    value={insurancePolicyNum}
                    onChange={this.handleInputChange}
                    variant="outlined"
                    fullWidth
                    placeholder="Enter policy number"
                  />
                  {this.renderCopyButton('insurancePolicyNum', insurancePolicyNum)}
                </div>
              </Box>

              {/* Referring Facility */}
              <Box className="form-field half">
                <label>Referring Facility</label>
                <div className="field-with-copy">
                  <TextField
                    name="referringFacility"
                    value={referringFacility}
                    onChange={this.handleInputChange}
                    variant="outlined"
                    fullWidth
                    placeholder="Enter referring facility"
                  />
                  {this.renderCopyButton('referringFacility', referringFacility)}
                </div>
              </Box>
            </Box>

            <Box display="flex" flexWrap="wrap" className="form-row">
              {/* Address */}
              <Box className="form-field half">
                <label>Address</label>
                <div className="field-with-copy">
                  <TextField
                    name="address"
                    value={address}
                    onChange={this.handleInputChange}
                    variant="outlined"
                    fullWidth
                    placeholder="Enter address"
                  />
                  {this.renderCopyButton('address', address)}
                </div>
              </Box>

              {/* City */}
              <Box className="form-field half">
                <label>City</label>
                <div className="field-with-copy">
                  <TextField
                    name="city"
                    value={city}
                    onChange={this.handleInputChange}
                    variant="outlined"
                    fullWidth
                    placeholder="Enter city"
                  />
                  {this.renderCopyButton('city', city)}
                </div>
              </Box>
            </Box>

            <Box display="flex" flexWrap="wrap" className="form-row">
              {/* Zip */}
              <Box className="form-field half">
                <label>Zip Code</label>
                <div className="field-with-copy">
                  <TextField
                    name="zip"
                    value={zip}
                    onChange={this.handleInputChange}
                    variant="outlined"
                    fullWidth
                    placeholder="Enter zip code"
                  />
                  {this.renderCopyButton('zip', zip)}
                </div>
              </Box>

              {/* Phone */}
              <Box className="form-field half">
                <label>Phone</label>
                <div className="field-with-copy">
                  <TextField
                    name="phone"
                    value={phone}
                    onChange={this.handleInputChange}
                    variant="outlined"
                    fullWidth
                    placeholder="Enter phone number"
                  />
                  {this.renderCopyButton('phone', phone)}
                </div>
              </Box>
            </Box>

            <Box display="flex" flexWrap="wrap" className="form-row">
              {/* Primary Diagnosis */}
              <Box className="form-field half">
                <label>Primary Diagnosis</label>
                <div className="field-with-copy">
                  <TextField
                    name="primaryDiagnosis"
                    value={primaryDiagnosis}
                    onChange={this.handleInputChange}
                    variant="outlined"
                    fullWidth
                    placeholder="Enter primary diagnosis"
                  />
                  {this.renderCopyButton('primaryDiagnosis', primaryDiagnosis)}
                </div>
              </Box>

              {/* Referring Physician NPI */}
              <Box className="form-field half">
                <label>Referring Physician NPI</label>
                <div className="field-with-copy">
                  <TextField
                    name="referringPhysicianNpi"
                    value={referringPhysicianNpi}
                    onChange={this.handleInputChange}
                    variant="outlined"
                    fullWidth
                    placeholder="Enter physician NPI"
                  />
                  {this.renderCopyButton('referringPhysicianNpi', referringPhysicianNpi)}
                </div>
              </Box>
            </Box>

            {/* Documents Section - Only show for DRAFT patients (Verify Details flow) */}
            {patient?.status === "DRAFT" && (
              <Box className="documents-section">
                <div className="documents-header">
                  <label>Uploaded Documents</label>
                  <input
                    type="file"
                    id="modal-document-upload"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    onChange={this.handleDocumentUpload}
                    style={{ display: "none" }}
                  />
                  <label htmlFor="modal-document-upload">
                    <Button
                      component="span"
                      className="add-document-btn"
                      disabled={uploadingDocument}
                      size="small"
                    >
                      {uploadingDocument ? (
                        <CircularProgress size={14} />
                      ) : (
                        <>
                          <AddIcon fontSize="small" /> Add
                        </>
                      )}
                    </Button>
                  </label>
                </div>
                {loadingDocuments ? (
                  <div className="documents-loading">
                    <CircularProgress size={20} />
                    <span>Loading documents...</span>
                  </div>
                ) : documents.length === 0 ? (
                  <span className="no-docs">No documents uploaded</span>
                ) : (
                  <div className="documents-list">
                    {documents.map((doc) => (
                      <div className="document-item" key={doc.id}>
                        <DescriptionIcon className="doc-icon" />
                        <span className="doc-name">{doc.uploadName}</span>
                        <IconButton
                          size="small"
                          onClick={() => this.handleViewDocument(doc)}
                          disabled={loadingDocumentId === doc.id}
                          title="View document"
                        >
                          {loadingDocumentId === doc.id ? (
                            <CircularProgress size={16} />
                          ) : (
                            <VisibilityIcon fontSize="small" />
                          )}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => this.handleDeleteDocument(doc)}
                          disabled={deletingDocumentId === doc.id}
                          title="Delete document"
                          className="delete-doc-btn"
                        >
                          {deletingDocumentId === doc.id ? (
                            <CircularProgress size={16} />
                          ) : (
                            <DeleteIcon fontSize="small" />
                          )}
                        </IconButton>
                      </div>
                    ))}
                  </div>
                )}
              </Box>
            )}

            {errors.submit && (
              <Box className="error-message">{errors.submit}</Box>
            )}
          </form>
        </DialogContent>
        
        <DialogActions className="modal-actions">
          <Button
            onClick={onClose}
            className="cancel-btn"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={this.handleSubmit}
            className="submit-btn"
            disabled={loading}
          >
            {loading && <CircularProgress size={20} className="btn-loader" />}
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

export default EditPatientModal;
